import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
  Popover,
  PopoverButton,
  PopoverPanel,
} from '@headlessui/react';
import useIsMobile from '@/hooks/useIsMobile';
import { WyscoutPlayerFixtureFilterOptions } from '@/services/api';
import WyscoutAdvancedFilters from './WyscoutAdvancedFilters';
import {
  activeWyscoutAdvancedFilterCount,
  WyscoutMatchFilters as FilterValues,
} from './wyscoutMatchFilters';

interface WyscoutMatchFiltersProps {
  values: FilterValues;
  options: WyscoutPlayerFixtureFilterOptions;
  activeCount: number;
  resultCount: number;
  totalCount: number;
  hidden: boolean;
  onChange: (patch: Partial<FilterValues>) => void;
  onClear: () => void;
  onHiddenChange: (hidden: boolean) => void;
}

const actionClass = 'min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700';
const activeActionClass = 'border-blue-600 bg-blue-50 text-blue-700 dark:border-blue-400 dark:bg-blue-900/30 dark:text-blue-200';
const controlClass = 'min-h-[44px] rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100';

const WyscoutMatchFilters: React.FC<WyscoutMatchFiltersProps> = ({
  values,
  options,
  activeCount,
  resultCount,
  totalCount,
  hidden,
  onChange,
  onClear,
  onHiddenChange,
}) => {
  const isMobile = useIsMobile(768);
  const [mobileOpen, setMobileOpen] = useState(false);
  const advancedCount = activeWyscoutAdvancedFilterCount(values);
  const advancedLabel = `All filters${advancedCount ? ` (${advancedCount})` : ''}`;

  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  if (hidden) {
    return (
      <>
        <div id="wyscout-match-filters" />
        <p role="status" aria-live="polite" className="mb-2 text-sm text-gray-600 dark:text-gray-300">{resultCount} of {totalCount} fixtures</p>
        <div className="flex flex-wrap items-center gap-3">
          {activeCount > 0 && <button type="button" className={actionClass} onClick={onClear}>Clear all filters</button>}
          <button type="button" className={actionClass} aria-expanded="false" aria-controls="wyscout-match-filters" onClick={() => onHiddenChange(false)}>
            Show filters{activeCount ? ` (${activeCount})` : ''}
          </button>
        </div>
      </>
    );
  }

  const chips: Array<{ label: string; clear: Partial<FilterValues> }> = [];
  if (values.participation !== 'all' && !(values.minMinutes !== null || values.scored || values.assisted || values.positions.length || values.cards !== 'all')) chips.push({ label: values.participation === 'played' ? 'Played' : 'Did not play', clear: { participation: 'all' } });
  if (values.minMinutes !== null) chips.push({ label: `Minutes: ${values.minMinutes}+`, clear: { minMinutes: null } });
  if (values.scored) chips.push({ label: 'Scored', clear: { scored: false } });
  if (values.assisted) chips.push({ label: 'Assisted', clear: { assisted: false } });
  if (values.clubIds.length) chips.push({ label: `Club: ${values.clubIds.map(id => options.clubs.find(option => option.id === id)?.name || id).join(', ')}`, clear: { clubIds: [] } });
  if (values.venues.length) chips.push({ label: `Venue: ${values.venues.map(value => `${value[0].toUpperCase()}${value.slice(1)}`).join(', ')}`, clear: { venues: [] } });
  if (values.results.length) chips.push({ label: `Result: ${values.results.map(value => `${value[0].toUpperCase()}${value.slice(1)}`).join(', ')}`, clear: { results: [] } });
  if (values.opponent.trim()) chips.push({ label: `Opponent: ${values.opponent.trim()}`, clear: { opponent: '' } });
  if (values.positions.length) chips.push({ label: `Position: ${values.positions.map(value => value.toUpperCase()).join(', ')}`, clear: { positions: [] } });
  const selectedSeasonNames = values.seasonIds.map(id => options.seasons.find(option => option.id === id)?.name?.trim() || 'Unavailable season');
  const selectedCompetitionNames = values.competitionIds.map(id => options.competitions.find(option => option.id === id)?.name?.trim() || 'Unavailable competition');
  if (values.seasonIds.length) chips.push({ label: `Season: ${selectedSeasonNames.join(', ')}`, clear: { seasonIds: [] } });
  if (values.competitionIds.length) chips.push({ label: `Competition: ${selectedCompetitionNames.join(', ')}`, clear: { competitionIds: [] } });
  if (values.dateFrom || values.dateTo) chips.push({ label: `Date: ${values.dateFrom || 'Any'} to ${values.dateTo || 'Any'}`, clear: { dateFrom: null, dateTo: null } });
  if (values.cards !== 'all') chips.push({ label: `Cards: ${values.cards[0].toUpperCase()}${values.cards.slice(1)}`, clear: { cards: 'all' } });
  if (values.statsStatuses.length) chips.push({ label: `Statistics: ${values.statsStatuses.map(value => `${value[0].toUpperCase()}${value.slice(1)}`).join(', ')}`, clear: { statsStatuses: [] } });

  const quickControls = (
    <div data-testid="wyscout-core-filters" className="flex flex-wrap items-end gap-2">
      <fieldset>
        <legend className="sr-only">Participation</legend>
        <div className="flex flex-wrap gap-2">
          {([['all', 'All fixtures'], ['played', 'Played'], ['did_not_play', 'Did not play']] as const).map(([value, label]) => (
            <button key={value} type="button" className={`${actionClass} ${values.participation === value ? activeActionClass : ''}`} aria-pressed={values.participation === value} onClick={() => onChange({ participation: value })}>{label}</button>
          ))}
        </div>
      </fieldset>
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300"><span className="sr-only">Minutes</span><select aria-label="Minutes" className={controlClass} value={values.minMinutes === null ? '' : [1, 30, 60, 90].includes(values.minMinutes) ? values.minMinutes : 'custom'} onChange={event => onChange({ minMinutes: event.target.value === '' ? null : event.target.value === 'custom' ? 0 : Number(event.target.value) })}><option value="">Any minutes</option><option value="1">1+ minutes</option><option value="30">30+ minutes</option><option value="60">60+ minutes</option><option value="90">90+ minutes</option><option value="custom">Custom</option></select></label>
      {values.minMinutes !== null && ![1, 30, 60, 90].includes(values.minMinutes) && <label className="text-sm text-gray-700 dark:text-gray-300"><span className="sr-only">Custom minimum minutes</span><input aria-label="Custom minimum minutes" type="number" min="0" max="120" step="1" className={`${controlClass} w-24`} value={values.minMinutes} onChange={event => onChange({ minMinutes: Number(event.target.value) })} /></label>}
      <button type="button" className={`${actionClass} ${values.scored ? activeActionClass : ''}`} aria-pressed={values.scored} onClick={() => onChange({ scored: !values.scored })}>Scored</button>
      <button type="button" className={`${actionClass} ${values.assisted ? activeActionClass : ''}`} aria-pressed={values.assisted} onClick={() => onChange({ assisted: !values.assisted })}>Assisted</button>
    </div>
  );

  return (
    <div id="wyscout-match-filters" className="rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/60">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div><h5 className="font-semibold text-gray-900 dark:text-gray-100">Match filters</h5><p role="status" aria-live="polite" className="text-sm text-gray-600 dark:text-gray-300">{resultCount} of {totalCount} fixtures</p>{activeCount > 0 && <p className="text-sm font-medium text-blue-700 dark:text-blue-300">{activeCount} {activeCount === 1 ? 'filter' : 'filters'} applied</p>}</div>
        <button type="button" className={actionClass} aria-expanded="true" aria-controls="wyscout-match-filters" onClick={() => onHiddenChange(true)}>Hide filters</button>
      </div>
      <div className="mt-4">{quickControls}</div>
      <div className="mt-3 flex flex-wrap items-center gap-2">
        {isMobile ? (
          <button type="button" className={actionClass} aria-expanded={mobileOpen} onClick={() => setMobileOpen(true)}>{advancedLabel}</button>
        ) : (
          <Popover className="relative">
            <PopoverButton className={actionClass}>{advancedLabel}</PopoverButton>
            <PopoverPanel role="dialog" aria-label="All match filters" className="absolute left-0 z-40 mt-2 max-h-[70vh] w-[min(42rem,calc(100vw-3rem))] overflow-y-auto rounded-xl border border-gray-200 bg-white p-5 shadow-xl dark:border-gray-700 dark:bg-gray-900">
              <WyscoutAdvancedFilters values={values} options={options} onChange={onChange} />
            </PopoverPanel>
          </Popover>
        )}
        {activeCount > 0 && <button type="button" className={actionClass} onClick={onClear}>Clear all filters</button>}
      </div>

      {chips.length > 0 && <div className="mt-3 flex flex-wrap gap-2" aria-label="Active filters">{chips.map(chip => <button key={chip.label} type="button" className="min-h-[44px] rounded-full bg-blue-100 px-3 py-2 text-sm font-medium text-blue-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-blue-900/40 dark:text-blue-200" aria-label={`Remove ${chip.label} filter`} onClick={() => onChange(chip.clear)}>{chip.label} <span aria-hidden="true">×</span></button>)}</div>}

      <Dialog open={isMobile && mobileOpen} onClose={setMobileOpen} className="relative z-50">
        <DialogBackdrop data-testid="match-filter-backdrop" className="fixed inset-0 bg-black/60" />
        <div className="fixed inset-0 flex items-end">
          <DialogPanel data-testid="wyscout-mobile-filter-panel" className="flex max-h-[90vh] w-full flex-col rounded-t-2xl bg-white shadow-2xl dark:bg-gray-900">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-700">
              <DialogTitle className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filter matches</DialogTitle>
              <button type="button" className={actionClass} onClick={() => setMobileOpen(false)}>Close</button>
            </div>
            <div className="overflow-y-auto px-4 py-5"><WyscoutAdvancedFilters values={values} options={options} onChange={onChange} /></div>
            <div className="sticky bottom-0 border-t border-gray-200 bg-white px-4 pb-[max(1rem,env(safe-area-inset-bottom))] pt-3 dark:border-gray-700 dark:bg-gray-900">
              <button type="button" className="min-h-[44px] w-full rounded-lg bg-blue-600 px-4 py-3 text-sm font-semibold text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-gray-900" onClick={() => setMobileOpen(false)}>Show {resultCount} matches</button>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  );
};

export default WyscoutMatchFilters;
